<script setup lang="ts">
import { computed } from 'vue';

// One 36x36 control in the top bar's icon strip. Renders as an anchor for external platform
// destinations, a router-link for in-app routes, and a plain button for dropdown triggers.
const props = defineProps({
  icon: {
    type: String,
    required: true
  },
  iconStyle: {
    type: String,
    default: 'fal'
  },
  /** Tooltip text, and the accessible name — the control has no visible label. */
  label: {
    type: String,
    required: true
  },
  href: {
    type: String,
    default: ''
  },
  /** Named route, for in-app destinations. */
  to: {
    type: String,
    default: ''
  },
  active: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['click']);

const tag = computed(() => {
  if (props.href) {
    return 'a';
  }
  return props.to ? 'router-link' : 'button';
});
</script>

<template>
  <component :is="tag"
    class="topbar-icon"
    :class="{ 'topbar-icon--active': active }"
    :href="href || undefined"
    :to="to ? { name: to } : undefined"
    :type="tag === 'button' ? 'button' : undefined"
    :aria-label="label"
    v-tooltip.bottom="label"
    @click="emit('click', $event)">
    <FontAwesomeIcon :icon="[iconStyle, icon]" />
    <!-- Optional mark pinned to the icon's corner, to distinguish otherwise identical icons. -->
    <slot name="badge"></slot>
  </component>
</template>

<style scoped lang="scss">
@use '../../assets/variables.module' as *;
.topbar-icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  padding: 8px;
  border: none;
  border-radius: 8px;
  background-color: transparent;
  color: hsl(210, 10%, 60%);
  font-size: 18px;
  line-height: 1;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background-color: hsl(210, 20%, 96%);
    color: hsl(210, 11%, 15%);
  }

  &:focus-visible {
    outline: 2px solid $primary-color;
    outline-offset: -2px;
  }
}
.topbar-icon--active {
  background-color: hsl(210, 20%, 96%);
  color: hsl(210, 11%, 15%);
}
</style>
